import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Activity, AlertCircle, ShieldCheck } from "lucide-react";

import { useAuth, useHasPermission } from "../../context/AuthContext";
import { fetchCallDetail } from "./supervision-api";
import { FlaggedCalls } from "./flagged-calls";
import type { SupervisedCall } from "./types";
import { usePersistentState } from "../../hooks/use-persistent-state";
import { AudioDock } from "./audio/audio-dock";
import { AudioProvider, useAudioSession } from "./audio/audio-provider";
import { CallDrawer } from "./drawer/call-drawer";
import { countCalls, orderingKey, sortCalls } from "./lib/alerts";
import { LiveInsights } from "./insights/live-insights";
import { AlertQueue } from "./queue/alert-queue";
import { ShiftLobby } from "./shift-lobby";
import { StatusStrip } from "./status-strip";
import { useShift } from "./use-shift";
import { useSupervisionStream } from "./use-supervision-stream";
import { CallWall } from "./wall/call-wall";
import { SUPERVISION_SECTION_STORAGE_KEY, type SupervisionSection } from "./supervision-section";
import { TRANSCRIPT_LANGUAGE_STORAGE_KEY, type TranscriptLanguageMode } from "./lib/transcript-language";
import { useDemoMode } from "../../lib/demo-mode";

/**
 * Supervisor console: every live call on the account on one screen, with the controls to
 * guide, listen to, take over and end any one of them.
 *
 * The audio session is provided at this level and not inside the drawer, which is what lets
 * a supervisor keep listening to one call while reading another.
 */
function SupervisionConsole() {
  const isDemoMode = useDemoMode();
  const shift = useShift();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // The stream is what does the supervising, so it starts with the shift and not before.
  const { calls, endedCalls = {}, status, error, authorization, rota, demoFills } = useSupervisionStream(shift.onShift);
  const session = useAudioSession();
  const audioCallId = session.callId ?? session.pendingCallId;
  const stopAudio = session.stop;

  const [view, setView] = usePersistentState<"wall" | "queue">("supervision.view", "wall");
  // My slice or the whole floor. Partitioning is display-only: assignment ranks and
  // highlights, it never gates an action, so a supervisor whose assignee goes quiet can
  // still flip to the whole wall. Claims remain what stops two voices on one call.
  const [showAll, setShowAll] = usePersistentState<boolean>("supervision.showAll", false);
  // Still remembered locally so the lobby can propose last time's answer, but the value
  // that matters now lives on the shift record -- capacity has to be visible to the floor
  // to be divisible across it.
  const [comfortableLoad, setComfortableLoad] = usePersistentState<number>(
    "supervision.comfortableLoad",
    6,
  );
  const effectiveComfortableLoad = Math.max(
    1,
    Math.min(25, Number(shift.rota?.mine?.comfortable_load ?? comfortableLoad) || 6),
  );
  const [focusedCallId, setFocusedCallId] = useState<string | null>(null);
  const [retainedCall, setRetainedCall] = useState<SupervisedCall | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const section: SupervisionSection = useMemo(() => {
    if (pathname.startsWith("/supervision/flagged")) return "flagged";
    if (pathname.startsWith("/supervision/insights")) return "insights";
    return "live";
  }, [pathname]);

  const setSection = useCallback((next: SupervisionSection) => {
    if (next === "flagged") navigate("/supervision/flagged");
    else if (next === "insights") navigate("/supervision/insights");
    else navigate("/supervision");
  }, [navigate]);
  const [languageMode, setLanguageMode] = usePersistentState<TranscriptLanguageMode>(
    TRANSCRIPT_LANGUAGE_STORAGE_KEY,
    "both",
  );
  const flaggedView = section === "flagged";
  const insightsView = section === "insights";
  const myUserId = shift.rota?.mine?.user_id ?? null;
  const findCallById = useCallback((id: string) => {
    return calls.find(c => c.call_id === id) ?? (isDemoMode ? demoFills.find(c => c.call_id === id) : undefined);
  }, [calls, isDemoMode, demoFills]);

  const selectCall = (id: string) => {
    const found = findCallById(id);
    setRetainedCall(found ?? {call_id: id});
    setFocusedCallId(id);
  };
  const selectFlagged = (call: SupervisedCall) => { setRetainedCall(call); setFocusedCallId(call.call_id); };
  const closeDrawer = () => { setFocusedCallId(null); setRetainedCall(null); };
  useEffect(() => {
    const current = findCallById(focusedCallId ?? "");
    if (current) setRetainedCall(current);
  }, [findCallById, focusedCallId]);

  // Re-sort only when priority actually changes. There is no layout animation here, so a
  // tile that moves because somebody spoke is a tile the supervisor misclicks.
  //
  // The order is held as a list of ids rather than of calls: ids are what stays stable
  // while the call objects behind them are replaced on every transcript line.
  const key = orderingKey(calls);
  const pinnedCallId = session.callId ?? focusedCallId;

  const orderedIds = useMemo(
    () => sortCalls(calls, { pinnedCallId }).map((call) => call.call_id),
    // `key` is the ordering signal. `calls` is read but deliberately not depended on, or
    // the wall would re-rank on every arriving turn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, pinnedCallId],
  );

  // Re-attach the latest call data to that order, and append anything that has joined the
  // roster since the last re-sort so a new call is never invisible.
  const visible = useMemo(() => {
    const byId = new Map(calls.map((call) => [call.call_id, call]));
    const inOrder = orderedIds
      .map((id) => byId.get(id))
      .filter((call): call is NonNullable<typeof call> => Boolean(call));
    const seen = new Set(inOrder.map((call) => call.call_id));
    return [...inOrder, ...calls.filter((call) => !seen.has(call.call_id))];
  }, [calls, orderedIds]);

  const currentCall = visible.find(call => call.call_id === focusedCallId) ?? (isDemoMode ? demoFills.find(call => call.call_id === focusedCallId) : undefined);
  const selected = currentCall ?? (retainedCall?.call_id === focusedCallId ? retainedCall : null);
  const endEvidence = focusedCallId ? endedCalls[focusedCallId] : undefined;
  const focusedCall = useMemo(() => selected ? {...selected,
    lifecycle: currentCall ? "live" as const : selected.lifecycle ?? "unavailable" as const,
    ...endEvidence,
  } : null, [selected, currentCall, endEvidence]);

  // One supervisor's slice of the floor. The server already deals every call to somebody
  // (holder-pinned, capacity-weighted), so this is a filter on that dealing, not a second
  // assignment. No assignment on any tile means the backend predates dealing -- hiding
  // everything would blank the wall for no reason, so the whole floor shows instead.
  //
  // An UNASSIGNED call shows to everybody, not nobody. A call that starts between roster
  // ticks arrives on the per-event path, which restates ownership from the last snapshot
  // and so cannot know about it yet -- filtering on equality alone hid exactly the call
  // that just came in, from every console at once, for up to a full tick.
  const wallCalls = useMemo(() => {
    if (showAll || !myUserId) return visible;
    if (!visible.some((call) => Boolean(call.assigned_to))) return visible;
    return visible.filter((call) => !call.assigned_to || call.assigned_to === myUserId);
  }, [visible, showAll, myUserId]);
  const hiddenCount = visible.length - wallCalls.length;

  /**
   * DEMO MODE: pad the wall with demo fill cards up to the supervisor's comfortable load.
   * Real calls are listed first (and keep their severity colours / badges).
   * Demo fills occupy the remaining slots only; they are excluded from sort/alert logic.
   * In production demoFills is always [] so this memo is essentially free.
   */
  const wallCallsWithDemoFills = useMemo(() => {
    if (!isDemoMode || demoFills.length === 0) return wallCalls;
    const liveIds = new Set(wallCalls.map((c) => c.call_id));
    const targetCapacity = showAll ? 25 : effectiveComfortableLoad;
    const fills = demoFills
      .filter((c) => !liveIds.has(c.call_id))
      .slice(0, Math.max(0, targetCapacity - wallCalls.length));
    return [...wallCalls, ...fills];
  }, [isDemoMode, demoFills, wallCalls, effectiveComfortableLoad, showAll]);

  useEffect(() => {
    if (authorization === "revoked") { setFocusedCallId(null); setRetainedCall(null); }
    if (audioCallId && endedCalls[audioCallId]) void stopAudio();
  }, [authorization, endedCalls, audioCallId, stopAudio]);

  // Losing authorization has to reach the *media* plane, not just the roster.
  //
  // The two are deliberately separate connections: audio is direct WebRTC to LiveKit, which
  // is what lets one supervisor watch twenty-five calls without twenty-five peer
  // connections. The price is that the server cannot hang up on us -- closing the SSE
  // stream leaves the room attached, still playing the customer's audio and, mid-takeover,
  // still publishing the supervisor's microphone into their call. Nothing below this line
  // is optional.
  const connected = Boolean(audioCallId);
  useEffect(() => {
    if (authorization === "ok" || !connected) return;
    void stopAudio();
  }, [authorization, connected, stopAudio]);

  useEffect(() => {
    const denied = () => {
      setFocusedCallId(null); setRetainedCall(null); setAccessDenied(true);
      void stopAudio();
    };
    window.addEventListener("supervision-access-denied", denied);
    return () => window.removeEventListener("supervision-access-denied", denied);
  }, [stopAudio]);
  const connectedOnRoster = calls.some(c => c.call_id === session.callId);
  useEffect(() => {
    if (!session.callId || connectedOnRoster) return;
    let cancelled = false;
    const check = async () => {
      try {
        const result = await fetchCallDetail(session.callId!);
        if (!cancelled && result.call.lifecycle?.startsWith("ended")) await stopAudio();
      } catch { /* Missing roster data alone does not establish termination. */ }
    };
    void check();
    const timer = setInterval(() => void check(), 20000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [session.callId, connectedOnRoster, stopAudio]);

  const counts = useMemo(() => countCalls(calls), [calls]);

  const leaveShift = async () => {
    // Order matters: the LiveKit room is a direct connection, so ending the shift without
    // dropping it would leave a supervisor listening to a call they are no longer on.
    if (session.callId) await session.stop();
    await shift.stop();
  };
  const connectedLabel = useMemo(
    () => calls.find((call) => call.call_id === session.callId)?.contact_name,
    [calls, session.callId],
  );

  if (accessDenied) return <p role="alert" className="p-6">Your supervision access could not be renewed. Refresh after signing in again.</p>;

  // The full-size switch lives in the application topbar. Keep a compact copy on small
  // screens, where the topbar hides it rather than crowding search and notifications.
  const mobileReviewNavigation = <nav aria-label="Supervision sections" className="flex shrink-0 gap-1 border-b border-line bg-elevated px-3 py-1.5 sm:hidden">
    <button className={section === "live" ? "rounded-md bg-soft px-2.5 py-1 text-xs font-medium text-ink" : "px-2.5 py-1 text-xs text-ink-3"} aria-pressed={section === "live"} onClick={() => setSection("live")}>Live</button>
    <button className={flaggedView ? "rounded-md bg-soft px-2.5 py-1 text-xs font-medium text-ink" : "px-2.5 py-1 text-xs text-ink-3"} aria-pressed={flaggedView} onClick={() => setSection("flagged")}>Flagged</button>
    <button className={insightsView ? "rounded-md bg-soft px-2.5 py-1 text-xs font-medium text-ink" : "px-2.5 py-1 text-xs text-ink-3"} aria-pressed={insightsView} onClick={() => setSection("insights")}>Insights</button>
  </nav>;
  if (flaggedView) return <div className="flex min-h-0 flex-1 flex-col bg-canvas">
    {mobileReviewNavigation}
    <div className="flex min-h-0 flex-1 gap-4 overflow-hidden p-4">
      <FlaggedCalls onSelect={selectFlagged} />
      <CallDrawer call={focusedCall} onClose={closeDrawer} languageMode={languageMode} onLanguageModeChange={setLanguageMode} />
    </div>
    <AudioDock onFocusCall={selectCall} label={connectedLabel} openCallId={focusedCall?.call_id} />
  </div>;

  if (insightsView) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-canvas">
        {mobileReviewNavigation}
        <LiveInsights
          calls={visible}
          currentUserId={myUserId}
          headroom={rota.headroom ?? shift.rota?.headroom}
          onOpenCall={(callId) => {
            selectCall(callId);
            setSection("live");
          }}
        />
        {shift.onShift ? <AudioDock onFocusCall={selectCall} label={connectedLabel} /> : null}
      </div>
    );
  }

  if (!shift.onShift) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-canvas">
        {mobileReviewNavigation}
        <ShiftLobby
          rota={shift.rota}
          busy={shift.busy}
          error={shift.error}
          comfortableLoad={effectiveComfortableLoad}
          onComfortableLoadChange={setComfortableLoad}
          onStart={() => void shift.start(effectiveComfortableLoad)}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-canvas">

      {mobileReviewNavigation}
      <div className="flex min-h-0 flex-1 flex-col gap-2 p-2 sm:p-3">
      <StatusStrip
        counts={counts}
        status={status}
        view={view}
        onViewChange={setView}
        declaredCapacity={effectiveComfortableLoad}
        onShift={rota.onShift}
        headroom={rota.headroom ?? shift.rota?.headroom}
        onEndShift={shift.onShift ? () => void leaveShift() : undefined}
        endingShift={shift.busy}
      />

      {error ? (
        <div className="flex items-center gap-2 rounded-lg border border-danger/40 bg-danger-soft px-3 py-2 text-xs text-danger">
          <AlertCircle size={14} />
          {error}
          {authorization !== "ok" ? (
            <span className="text-ink-2">Audio was disconnected.</span>
          ) : null}
        </div>
      ) : null}

      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <div className="w-full h-full flex min-h-0 flex-1 flex-col overflow-y-auto hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {view === "wall" ? (
            <div className="flex min-h-0 flex-1 flex-col h-full">
              <div className="mb-1 flex shrink-0 items-center gap-2 px-0.5 text-[11px] text-ink-3">
                <button
                  type="button"
                  onClick={() => setShowAll((current) => !current)}
                  className="rounded-full border border-line px-2.5 py-1 font-medium text-ink-2 transition hover:text-ink"
                  data-testid="supervision-show-all-toggle"
                >
                  {showAll ? "Showing whole floor" : "Showing my slice"}
                </button>
                {hiddenCount > 0 ? (
                  <span data-testid="supervision-hidden-count">
                    {hiddenCount} with {hiddenCount === 1 ? "a colleague" : "colleagues"}
                  </span>
                ) : null}
              </div>
              <CallWall
                calls={wallCallsWithDemoFills}
                selectedCallId={focusedCallId}
                connectedCallId={session.callId}
                tier={session.tier}
                currentUserId={myUserId}
                sliced={!showAll && wallCalls.length !== visible.length}
                onSelect={selectCall}
                onOpenQueue={() => setView("queue")}
                languageMode={languageMode}
                maxCalls={showAll ? 25 : effectiveComfortableLoad}
                isDemoMode={isDemoMode}
              />
            </div>
          ) : (
            <AlertQueue
              calls={visible}
              selectedCallId={focusedCallId}
              onSelect={selectCall}
            />
          )}
        </div>
        <AnimatePresence>
          {focusedCall && (
            <CallDrawer call={focusedCall} onClose={closeDrawer} languageMode={languageMode} onLanguageModeChange={setLanguageMode} />
          )}
        </AnimatePresence>
      </div>

      {shift.onShift ? <AudioDock onFocusCall={selectCall} label={connectedLabel} openCallId={focusedCall?.call_id} /> : null}
      </div>
    </div>
  );
}

import { DemoSupervisionConsole } from "./demo-supervision-console";

export function SupervisionPage() {
  const {user, account} = useAuth();
  const canView = useHasPermission("supervision.view");
  const isDemo = useDemoMode();

  if (!canView && !isDemo) return null;
  return (
    <AudioProvider key={`${user?.id}:${account?.id}:${account?.organization_id ?? user?.organization_id}`} >
      {isDemo ? <DemoSupervisionConsole /> : <SupervisionConsole />}
    </AudioProvider>
  );
}

